import React from "react";
import { Plan } from "../../Admin/types";
import { useIsMobile } from "../../hooks/useIsMobile";
import { cn } from "../../utils/classMerger";
import { PricingTier } from "./PricingTier";

interface PricingPlansProps {
    plans: Plan[];
    selectedPlanId: string | null;
    setSelectedPlanId: (planId: string) => void;
    buttons?: boolean;
    futurePlan?: string | null;
}

const PricingPlans: React.FC<PricingPlansProps> = ({
  plans,
  selectedPlanId,
  setSelectedPlanId,
  buttons = true,
  futurePlan = null,
}) => {
  // get screen width from hook
  const isMobile = useIsMobile(768); // <= 768px
  const isTablet = useIsMobile(1024); // <= 1024px

  const getFeatures = (notes: string): string[] => {
    return notes
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.startsWith("- "))
      .map((line) => line.substring(2).trim());
  };

  // sort plans based on price
  plans.sort((a, b) => Number(a.price) - Number(b.price));

  // Columns follow the plan count so a short list centers through mx-auto
  // instead of leaving empty columns on the right. Literal class names so
  // Tailwind generates them.
  const columns = isMobile ? 1 : Math.max(1, Math.min(plans.length, isTablet ? 2 : 4));
  const gridColsClass: Record<number, string> = {
    1: "grid-cols-1",
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
  };

  return (
    <section
      className={cn("grid", gridColsClass[columns], "gap-4 mx-auto md:gap-7 items-stretch")}
    >
      {plans.map((plan) => (
        <PricingTier
          id={plan?.id || ""}
          key={plan.id}
          name={plan.name}
          price={plan.price}
          description={plan.description}
          features={getFeatures(plan.userNotes)}
          highlighted={plan.id === selectedPlanId}
          ctaText={plan.id === selectedPlanId ? "Selected" : "Select Plan"}
          onSelect={() => setSelectedPlanId(plan?.id || "")}
          buttons={buttons}
          futurePlan={futurePlan}
        />
      ))}
    </section>
  );
};;

export default PricingPlans;
